import { useRef, useEffect, useCallback, useState } from 'react';
import OverlayItem from './OverlayItem';
import { OVERLAY_DEFAULT_SIZE } from './overlayDefaults';
import { FRAME_ASPECT } from './editorModel';
import styles from './LayoutEditor.module.css';

function fmtTime(sec) {
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export default function LayoutCanvas({
  overlays,
  selectedId,
  onSelect,
  onAddOverlay,
  onPositionChange,
  frameUrl,
  frameLoading,
  layers = {},
  previewError,
  previewStale = false,
  sessionDuration,
  previewTimeSec,
  onTimeChange,
  onRefreshFrame,
  previewDisabled = false,
  previewBlockedMessage = '',
}) {
  const canvasRef = useRef(null);
  const viewportRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 1, height: 1 });

  useEffect(() => {
    const update = () => {
      if (viewportRef.current) {
        const r = viewportRef.current.getBoundingClientRect();
        const width = Math.max(1, Math.min(r.width, r.height * FRAME_ASPECT));
        setCanvasSize({ width, height: width / FRAME_ASPECT });
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('overlayType');
    if (!type) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const defaultSize = OVERLAY_DEFAULT_SIZE[type] ?? { width: 0.15, height: 0.10 };

    // Center overlay on drop point, clamped to frame bounds
    let x = (e.clientX - rect.left) / rect.width - defaultSize.width / 2;
    let y = (e.clientY - rect.top) / rect.height - defaultSize.height / 2;
    x = Math.max(0, Math.min(x, 1 - defaultSize.width));
    y = Math.max(0, Math.min(y, 1 - defaultSize.height));

    onAddOverlay(type, { x, y, ...defaultSize });
  }, [onAddOverlay]);

  return (
    <div className={styles.canvasPanel}>
      <div ref={viewportRef} className={styles.canvasViewport}>
      <div
        ref={canvasRef}
        className={styles.canvasFrame}
        style={{ width: canvasSize.width, height: canvasSize.height }}
        tabIndex={0}
        data-layout-canvas
        aria-label="Layout canvas. Arrow keys move the selected overlay; Shift moves ten pixels."
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => onSelect(null)}
        onMouseDown={e => e.currentTarget.focus()}
      >
        {frameUrl
          ? <img src={frameUrl} alt="" className={styles.frameImg} draggable={false} />
          : (
            <div className={styles.framePlaceholder}>
              {previewBlockedMessage || (frameLoading ? 'Loading preview…' : 'Preview unavailable')}
            </div>
          )
        }

        {canvasSize.width > 1 && overlays.map(overlay => (
          <OverlayItem
            key={overlay._id}
            overlay={overlay}
            layer={layers[overlay._id]}
            canvasSize={canvasSize}
            isSelected={selectedId === overlay._id}
            onSelect={onSelect}
            onPositionChange={onPositionChange}
          />
        ))}
      </div>
      </div>

      <div className={styles.previewFeedback} role="status" aria-live="polite">
        {previewError ? (
          <span className={styles.previewFailure}>{previewError}{frameUrl ? ' Last successful preview retained.' : ''}</span>
        ) : previewBlockedMessage || (frameLoading ? 'Updating preview…' : previewStale ? 'Preview out of date' : 'Preview up to date')}
      </div>

      <div className={styles.scrubber}>
        <span className={styles.scrubLabel}>Time</span>
        {sessionDuration > 0 ? (
          <input
            type="range"
            className={styles.scrubSlider}
            min="0"
            max={Math.floor(sessionDuration)}
            step="1"
            value={previewTimeSec}
            aria-label="Preview time"
            onChange={e => onTimeChange(Number(e.target.value))}
          />
        ) : (
          <input
            type="number"
            className={styles.scrubInput}
            min="0"
            step="1"
            value={previewTimeSec}
            aria-label="Preview time in seconds"
            onChange={e => onTimeChange(Math.max(0, parseFloat(e.target.value) || 0))}
          />
        )}
        <span className={styles.scrubTime}>{fmtTime(previewTimeSec)}</span>
        <button
          className={styles.refreshBtn}
          onClick={onRefreshFrame}
          disabled={frameLoading || previewDisabled}
          title={previewDisabled ? previewBlockedMessage : ''}
        >
          {frameLoading ? 'Updating…' : previewError ? 'Retry preview' : 'Refresh preview'}
        </button>
      </div>
    </div>
  );
}
